import { Box, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import PrimaryHeading from '../../settings/commonPrimaryHeading';
import { Close } from '@mui/icons-material';
import { t } from 'i18next';
import FTextInput from '../../commonComponents/F_TextInput';
import FButton from '../../commonComponents/F_Button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../utils/Api/POS';
import { toast } from 'react-toastify';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
    bgcolor: '#FFFFFF',
    borderRadius: 3,
    boxShadow: 0,
    p: 4,
};

const SubscriptionUsageCountModal = ({
    open = false,
    onClose = () => {},
    selectedService = null,
    fetchServiceSubscription = () => {},
}) => {
    const [loading, setLoading] = useState(false);
    const bookingListCount =
        selectedService?.transactions?.filter((transaction) => !transaction?.deletedAt).length ?? 0;
    const currentMaxCredit = Number(selectedService?.subscription?.subscriptionCredit ?? 0);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                maxCredit: Yup.number()
                    .typeError(t('Calendar.MaxCreditTypeError'))
                    .required(t('Calendar.MaxCreditRequired'))
                    .integer(t('Calendar.MaxCreditIntegerOnly'))
                    .min(bookingListCount, t('Calendar.MaxCreditMinUsed')),
            }),
        [bookingListCount],
    );

    const formik = useFormik({
        initialValues: { maxCredit: currentMaxCredit },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                await api.putApiServiceSubscriptionId(selectedService?.subscription?.id, {
                    subscriptionCredit: Number(values.maxCredit),
                });
                toast.success(t('Calendar.SubscriptionUpdateSuccess'));
                onClose();
                fetchServiceSubscription();
            } catch (error) {
                console.error('Error:', error);
                toast.error(t('Calendar.FailedSubscriptionUpdate'));
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (open && selectedService?.subscription) {
            formik.resetForm({
                values: { maxCredit: currentMaxCredit },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedService, currentMaxCredit]);

    return (
        <Modal disableAutoFocus open={open} onClose={onClose}>
            <Paper
                sx={[
                    style,
                    {
                        zIndex: 12,
                        overflowY: { xs: 'auto', sm: 'auto' },
                        maxHeight: { xs: '90vh', sm: '85vh' },
                        position: 'relative',
                        width: { xs: '90%', sm: '30%' },
                        minWidth: { xs: 'auto', sm: 360 },
                        borderRadius: '25px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    },
                ]}
            >
                <PrimaryHeading text={t('Calendar.EditSubscriptionMaxCount')} />
                <IconButton
                    sx={{ position: 'absolute', top: 15, right: 8 }}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    onClick={onClose}
                >
                    <Close />
                </IconButton>
                <Stack spacing={3} marginTop={2}>
                    <Box>
                        <Typography>{t('Calendar.ServiceName')}</Typography>
                        <FTextInput value={selectedService?.subscription?.serviceName} disabled sx={{ m: 0 }} />
                    </Box>
                    <Box>
                        <Typography>{t('Calendar.SubscriptionCount')}</Typography>
                        <FTextInput
                            name="maxCredit"
                            value={formik.values.maxCredit}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^\d*$/.test(val)) {
                                    formik.setFieldValue('maxCredit', val === '' ? '' : Number(val));
                                }
                            }}
                            onBlur={formik.handleBlur}
                            sx={{ m: 0 }}
                        />
                        {formik.errors.maxCredit && formik.touched.maxCredit && (
                            <Typography color="error" fontSize="12px">
                                {formik.errors.maxCredit}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 2,
                        mt: 4,
                    }}
                >
                    <FButton
                        sx={{ width: '150px', backgroundColor: '#D9D9D9' }}
                        titleColor="#fff"
                        onClick={onClose}
                        title={t('Customer.SaveChangeCan')}
                    />
                    <FButton
                        sx={{ width: '150px', backgroundColor: '#44B904' }}
                        titleColor="#fff"
                        onClick={formik.handleSubmit}
                        title={t('Customer.SaveChangeConf')}
                        loading={loading}
                    />
                </Stack>
            </Paper>
        </Modal>
    );
};

export default SubscriptionUsageCountModal;
